import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase } from '../lib/typeHelper'

export interface SalesLeadTag {
  id: string
  name: string
}

const TAGS_URL = '/api/v1/insure/internal/sales-lead-tags/'

function getSalesLeadTags(): Promise<SalesLeadTag[]> {
  return axios.get(TAGS_URL).then((response) => toCamelCase(response.data) as SalesLeadTag[])
}

export function useSalesLeadTags() {
  return useQuery({
    queryKey: [CacheKeys.SALES_LEAD_TAGS],
    queryFn: getSalesLeadTags,
  })
}

export function useCreateSalesLeadTag() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (name: string) =>
      axios.post(TAGS_URL, { name }).then((response) => toCamelCase(response.data) as SalesLeadTag),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEAD_TAGS] })
    },
  })
}

export function useSetLeadTags() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ leadId, tagIds }: { leadId: string; tagIds: string[] }) =>
      axios.put(`/api/v1/insure/internal/sales-leads/${leadId}/tags/`, { tag_ids: tagIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}

// A stable, soft tailwind color pair derived from the tag name so each tag reads
// consistently across the app (GitHub-label style) without storing a color.
const TAG_PALETTE = [
  'bg-slate-100 text-slate-700',
  'bg-red-100 text-red-700',
  'bg-orange-100 text-orange-700',
  'bg-amber-100 text-amber-700',
  'bg-lime-100 text-lime-700',
  'bg-emerald-100 text-emerald-700',
  'bg-teal-100 text-teal-700',
  'bg-cyan-100 text-cyan-700',
  'bg-blue-100 text-blue-700',
  'bg-indigo-100 text-indigo-700',
  'bg-violet-100 text-violet-700',
  'bg-fuchsia-100 text-fuchsia-700',
  'bg-pink-100 text-pink-700',
  'bg-rose-100 text-rose-700',
]

export function tagColor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0
  }
  return TAG_PALETTE[Math.abs(hash) % TAG_PALETTE.length]
}
